import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import type {
  AiAssistantConfigDto,
  AiAssistantModeDto,
  CompanyModuleDto,
  CompanyModuleStatusDto,
} from '@/shared/api/companies';

import { useChannelsOfCompanyList } from '../model/useChannelsOfCompanyList';

const DEFAULT_AI_ASSISTANT_CONFIG: AiAssistantConfigDto = {
  mode: 'operator_drafts',
  handoff: {
    pause_after_operator_reply_seconds: 1800,
  },
  autonomous_reply: {
    enabled: false,
  },
  crm_field_writes: {
    enabled: false,
    min_confidence: 0.85,
    fill_missing_only: true,
  },
};

const AI_ASSISTANT_MODES: AiAssistantModeDto[] = [
  'hints_only',
  'operator_drafts',
  'delayed_autonomous',
];

function parseIntegerDraft(value: string, min: number, max: number) {
  if (value.trim() === '') return null;

  const parsed = Number(value);

  if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) return null;
  if (parsed < min || parsed > max) return null;

  return parsed;
}

function parseMode(config: Record<string, unknown>, autonomousReply: Record<string, unknown>) {
  if (
    typeof config.mode === 'string' &&
    AI_ASSISTANT_MODES.includes(config.mode as AiAssistantModeDto)
  ) {
    return config.mode as AiAssistantModeDto;
  }

  return autonomousReply.enabled === true
    ? 'delayed_autonomous'
    : DEFAULT_AI_ASSISTANT_CONFIG.mode;
}

function parseConfig(config: Record<string, unknown>): AiAssistantConfigDto {
  const handoff = typeof config.handoff === 'object' && config.handoff !== null
    ? config.handoff as Record<string, unknown>
    : {};
  const autonomousReply = typeof config.autonomous_reply === 'object' && config.autonomous_reply !== null
    ? config.autonomous_reply as Record<string, unknown>
    : {};
  const crmFieldWrites = typeof config.crm_field_writes === 'object' && config.crm_field_writes !== null
    ? config.crm_field_writes as Record<string, unknown>
    : {};
  const minConfidence = Number(
    crmFieldWrites.min_confidence ??
      DEFAULT_AI_ASSISTANT_CONFIG.crm_field_writes.min_confidence
  );
  const mode = parseMode(config, autonomousReply);

  return {
    mode,
    handoff: {
      pause_after_operator_reply_seconds: Number(
        handoff.pause_after_operator_reply_seconds ??
          DEFAULT_AI_ASSISTANT_CONFIG.handoff.pause_after_operator_reply_seconds
      ),
    },
    autonomous_reply: {
      enabled: mode === 'delayed_autonomous',
    },
    crm_field_writes: {
      enabled: crmFieldWrites.enabled === true,
      min_confidence: Number.isFinite(minConfidence)
        ? minConfidence
        : DEFAULT_AI_ASSISTANT_CONFIG.crm_field_writes.min_confidence,
      fill_missing_only: true,
    },
  };
}

export function AiAssistantPolicyPanel({
  companyId,
  module,
  safeAutomationStatus,
  accentColor,
  disabled,
  onSave,
}: {
  companyId: string;
  module: CompanyModuleDto;
  safeAutomationStatus?: CompanyModuleStatusDto;
  accentColor: string;
  disabled: boolean;
  onSave: (input: {
    config: AiAssistantConfigDto;
    scope: { channel_ids: string[] };
  }) => void;
}) {
  const { t } = useTranslation('company');
  const channelsQuery = useChannelsOfCompanyList(companyId);
  const channels = channelsQuery.channelsOfCompanyData ?? [];
  const initial = useMemo(
    () => parseConfig(module.config),
    [
      module.config.mode,
      module.config.handoff,
      module.config.autonomous_reply,
      module.config.crm_field_writes,
    ]
  );
  const [pauseSeconds, setPauseSeconds] = useState(
    String(initial.handoff.pause_after_operator_reply_seconds)
  );
  const [mode, setMode] = useState<AiAssistantModeDto>(initial.mode);
  const [crmFieldWritesEnabled, setCrmFieldWritesEnabled] = useState(
    initial.crm_field_writes.enabled
  );
  const [selectedChannelIds, setSelectedChannelIds] = useState(
    module.scope.channel_ids
  );
  const pauseSecondsValue = parseIntegerDraft(pauseSeconds, 0, 86400);
  const canSave = !disabled && pauseSecondsValue !== null && !channelsQuery.isPending;
  const allChannelIds = channels.map((channel) => channel.id);
  const allChannelsSelected =
    allChannelIds.length > 0 &&
    allChannelIds.every((channelId) => selectedChannelIds.includes(channelId));

  useEffect(() => {
    setPauseSeconds(String(initial.handoff.pause_after_operator_reply_seconds));
    setMode(initial.mode);
    setCrmFieldWritesEnabled(initial.crm_field_writes.enabled);
  }, [
    initial.mode,
    initial.handoff.pause_after_operator_reply_seconds,
    initial.crm_field_writes.enabled,
  ]);

  useEffect(() => {
    setSelectedChannelIds(module.scope.channel_ids);
  }, [module.scope.channel_ids]);

  const toggleChannel = (channelId: string) => {
    setSelectedChannelIds((prev) =>
      prev.includes(channelId)
        ? prev.filter((item) => item !== channelId)
        : [...prev, channelId]
    );
  };
  const delayedAutonomousSelected = mode === 'delayed_autonomous';
  const safeAutomationWillBeEnabled =
    delayedAutonomousSelected && safeAutomationStatus !== 'enabled';

  return (
    <Stack
      spacing={1.5}
      sx={{
        mt: 0.5,
        p: 1.5,
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.text.primary, 0.1),
        borderRadius: 1,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            fontWeight={800}
            sx={{ color: 'text.primary' }}
          >
            {t('modules.aiAssistantPolicy.title')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary' }}
          >
            {t('modules.aiAssistantPolicy.caption')}
          </Typography>
        </Box>
        <Chip
          size="small"
          variant="outlined"
          label={t('modules.aiAssistantPolicy.selectedChannels', {
            count: selectedChannelIds.length,
          })}
          sx={{
            flexShrink: 0,
            borderRadius: 1,
            borderColor: alpha(accentColor, 0.24),
            color: accentColor,
            bgcolor: alpha(accentColor, 0.08),
          }}
        />
      </Stack>

      {channelsQuery.isError ? (
        <Typography variant="body2" color="error">
          {t('modules.aiAssistantPolicy.channelsError')}
        </Typography>
      ) : null}

      {!channelsQuery.isPending && !channelsQuery.isError && channels.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('modules.aiAssistantPolicy.channelsEmpty')}
        </Typography>
      ) : null}

      {channels.length > 0 ? (
        <FormGroup
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 0.5,
          }}
        >
          {channels.map((channel) => (
            <FormControlLabel
              key={channel.id}
              sx={{
                m: 0,
                minWidth: 0,
                alignSelf: 'flex-start',
                '& .MuiFormControlLabel-label': {
                  minWidth: 0,
                  fontSize: 14,
                  lineHeight: 1.35,
                },
              }}
              control={(
                <Checkbox
                  size="small"
                  checked={selectedChannelIds.includes(channel.id)}
                  disabled={disabled}
                  onChange={() => toggleChannel(channel.id)}
                />
              )}
              label={(
                <Stack spacing={0.1} sx={{ minWidth: 0 }}>
                  <Typography
                    component="span"
                    sx={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {channel.name || channel.phone_number || channel.id}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: 'text.secondary',
                      fontSize: 12,
                      lineHeight: 1.25,
                    }}
                  >
                    {channel.protocol} / {channel.status}
                  </Typography>
                </Stack>
              )}
            />
          ))}
        </FormGroup>
      ) : null}

      <Stack spacing={1}>
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight={800}
            sx={{ color: 'text.primary' }}
          >
            {t('modules.aiAssistantPolicy.modeTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('modules.aiAssistantPolicy.modeCaption')}
          </Typography>
        </Box>

        <ToggleButtonGroup
          exclusive
          value={mode}
          disabled={disabled}
          onChange={(_, nextMode: AiAssistantModeDto | null) => {
            if (nextMode) setMode(nextMode);
          }}
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 0.75,
            '& .MuiToggleButtonGroup-grouped': {
              m: 0,
              border: '1px solid',
              borderColor: (theme) => alpha(theme.palette.text.primary, 0.14),
              borderRadius: 1,
            },
          }}
        >
          {AI_ASSISTANT_MODES.map((modeOption) => (
            <ToggleButton
              key={modeOption}
              value={modeOption}
              aria-label={t(`modules.aiAssistantPolicy.modes.${modeOption}.label`)}
              sx={{
                minHeight: 92,
                justifyContent: 'flex-start',
                alignItems: 'stretch',
                px: 1.25,
                py: 1,
                textAlign: 'left',
                textTransform: 'none',
                '&.Mui-selected': {
                  borderColor: alpha(accentColor, 0.38),
                  bgcolor: alpha(accentColor, 0.1),
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: alpha(accentColor, 0.14),
                  },
                },
              }}
            >
              <Stack spacing={0.45} sx={{ minWidth: 0 }}>
                <Typography
                  component="span"
                  fontWeight={850}
                  sx={{ fontSize: 14, lineHeight: 1.25 }}
                >
                  {t(`modules.aiAssistantPolicy.modes.${modeOption}.label`)}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    color: 'text.secondary',
                    fontSize: 12,
                    lineHeight: 1.35,
                    whiteSpace: 'normal',
                  }}
                >
                  {t(`modules.aiAssistantPolicy.modes.${modeOption}.description`)}
                </Typography>
              </Stack>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Alert severity={delayedAutonomousSelected ? 'warning' : 'info'}>
          {t(`modules.aiAssistantPolicy.modeImpact.${mode}`)}
        </Alert>

        {safeAutomationWillBeEnabled ? (
          <Alert severity="warning">
            {t('modules.aiAssistantPolicy.safeAutomationRequired')}
          </Alert>
        ) : null}
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        sx={{
          px: 1.25,
          py: 1,
          border: '1px dashed',
          borderColor: (theme) => alpha(theme.palette.text.primary, 0.16),
          borderRadius: 1,
          bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800}>
            {t('modules.aiAssistantPolicy.crmTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('modules.aiAssistantPolicy.crmCaption')}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mt: 0.35 }}
          >
            {t('modules.aiAssistantPolicy.crmAllowedFields')}
          </Typography>
        </Box>
        <FormControlLabel
          sx={{
            m: 0,
            minHeight: 38,
            '& .MuiFormControlLabel-label': {
              fontSize: 14,
              fontWeight: 700,
            },
          }}
          control={(
            <Switch
              size="small"
              checked={crmFieldWritesEnabled}
              disabled={disabled}
              onChange={(event) => setCrmFieldWritesEnabled(event.target.checked)}
            />
          )}
          label={t('modules.aiAssistantPolicy.crmFieldWrites')}
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(0, 1fr) auto auto',
          },
          gap: 1,
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          type="number"
          label={t('modules.aiAssistantPolicy.handoffPause')}
          value={pauseSeconds}
          disabled={disabled}
          error={pauseSeconds !== '' && pauseSecondsValue === null}
          inputProps={{ min: 0, max: 86400, step: 1 }}
          onChange={(event) => setPauseSeconds(event.target.value)}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />
        <Button
          variant="outlined"
          disabled={disabled || channels.length === 0}
          onClick={() => {
            setSelectedChannelIds(allChannelsSelected ? [] : allChannelIds);
          }}
          sx={{
            minHeight: 38,
            borderRadius: 1,
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {allChannelsSelected
            ? t('modules.aiAssistantPolicy.clearChannels')
            : t('modules.aiAssistantPolicy.allChannels')}
        </Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={() => {
            if (!canSave) return;

            onSave({
              scope: {
                channel_ids: selectedChannelIds,
              },
              config: {
                mode,
                handoff: {
                  pause_after_operator_reply_seconds: pauseSecondsValue,
                },
                autonomous_reply: {
                  enabled: delayedAutonomousSelected,
                },
                crm_field_writes: {
                  enabled: crmFieldWritesEnabled,
                  min_confidence: initial.crm_field_writes.min_confidence,
                  fill_missing_only: true,
                },
              },
            });
          }}
          sx={{
            minHeight: 38,
            borderRadius: 1,
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {t('modules.aiAssistantPolicy.save')}
        </Button>
      </Box>
    </Stack>
  );
}
